import { FieldShell } from "./FieldShell";
import { fieldIds } from "./fieldStyles";

export interface ButtonGroupOption<T extends string> {
  value: T;
  label: string;
}

interface ButtonGroupInputProps<T extends string> {
  id: string;
  label: string;
  value: T | null;
  onChange: (value: T) => void;
  options: readonly ButtonGroupOption<T>[];
  error?: string | null;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  containerClassName?: string;
  children?: never;
}

const BASE = "flex-1 min-h-[44px] px-3 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer text-center focus:outline-none focus:ring-2 focus:ring-brand-green-50 disabled:cursor-default disabled:opacity-60";
const ACTIVE = "bg-brand-green-10 text-brand-green border-brand-green font-semibold";
const INACTIVE = "bg-white text-gray-600 border-gray-300 hover:bg-gray-50";

/** Segmented radio group (Relation, IHQ Type, Sex). */
export function ButtonGroupInput<T extends string>({ id, label, value, onChange, options, error, hint, required, disabled, containerClassName }: ButtonGroupInputProps<T>) {
  const ids = fieldIds(id, error, hint);

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName} labelAs="span">
      <div id={id} role="radiogroup" aria-labelledby={`${id}-label`} aria-required={required || undefined} aria-invalid={error ? true : undefined} aria-describedby={ids.describedBy} className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`${BASE} ${value === option.value ? ACTIVE : INACTIVE}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </FieldShell>
  );
}
